import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { ProductKeys3P, type ProductKeys } from '../../../common/constants/products';

import { getFederatedEmailResultCount } from './get-federated-email-result-count';

const FEDERATED_EMAIL_PRODUCTS = new Set<ProductKeys>([ProductKeys3P.Gmail]);

export type FederatedEmailPageInfo = {
	endCursor?: string | null;
	hasNextPage?: boolean;
};

type UseFederatedEmailResultCountProps = {
	selectedProduct: ProductKeys | undefined;
	rawResultsLength: number;
	resultsLength: number;
	pageInfo?: FederatedEmailPageInfo | null;
	isLoadingNext: boolean;
	setFederatedGmailCount?: (count: number | null) => void;
	setHasMoreGmailResults?: (hasMore: boolean) => void;
};

export function useFederatedEmailResultCount({
	selectedProduct,
	rawResultsLength,
	resultsLength,
	pageInfo,
	isLoadingNext,
	setFederatedGmailCount,
	setHasMoreGmailResults,
}: UseFederatedEmailResultCountProps): void {
	useEffect(() => {
		const isFederatedEmailCountEnabled = fg('formula_3p_gmail_result_count');
		const isSelectedFederatedEmail =
			selectedProduct !== undefined && FEDERATED_EMAIL_PRODUCTS.has(selectedProduct);
		const hasNextPage = pageInfo?.hasNextPage === true;
		if (!setFederatedGmailCount) {
			return;
		}

		if (!isFederatedEmailCountEnabled || !isSelectedFederatedEmail) {
			setFederatedGmailCount(null);
			setHasMoreGmailResults?.(false);
			return;
		}

		const { count, hasMore } = getFederatedEmailResultCount({
			rawCount: rawResultsLength,
			exactCount: resultsLength,
			hasNextPage,
		});

		setFederatedGmailCount(count);
		setHasMoreGmailResults?.(hasMore);
	}, [
		isLoadingNext,
		pageInfo,
		rawResultsLength,
		resultsLength,
		selectedProduct,
		setFederatedGmailCount,
		setHasMoreGmailResults,
	]);
}
